import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { CaseDetailsDto } from '@shared/models/caseDetails-dto';
import { CorrespondenceDto } from '@shared/models/correspondence-dto';
import { DocumentDetailsDto } from '@shared/models/document-details-dto';
import { DraftConfirmatoryPortalDto } from '@shared/models/applicant-request-dto';

@Injectable({
    providedIn: 'root'
})
export class CaseService {

    constructor(private http: HttpClient) {
    }

    loadCaseDetails(caseId: number): Observable<CaseDetailsDto> {
        return this.http.get<CaseDetailsDto>(`api/portal/case/${ caseId }/details`);
    }

    loadInitialCaseDetails(caseId: number): Observable<CaseDetailsDto> {
        return this.http.get<CaseDetailsDto>(`api/portal/case/${ caseId }/initial-request`);
    }

    /**
     * Informs backend that user withdrawn his request either initial or cornfirmatory
     * @param caseId is the id of the case
     * @returns the updated case after withdraw
     */
    withdrawRequest(caseId: number): Observable<CaseDetailsDto> {
        return this.http.post<CaseDetailsDto>(`api/portal/case/${ caseId }/withdraw-request`, {});
    }

    /**
     * Creates a new draft correspondence
     * @param caseId the id of the case that correspondence belongs
     * @param correspondenceContent the content of the draft correspondence
     * @param attachments The attachments
     * @param replyingCorrespondenceId the id of the correspondence that the draft content belongs
     */
    createDraftCorrespondence(caseId: number, correspondenceContent: string,
                              attachments: DocumentDetailsDto[], replyingCorrespondenceId: number): Observable<CaseDetailsDto> {
        return this.http.post<CaseDetailsDto>(`api/portal/correspondence/${ caseId }/draft-correspondence`,
            this.createFormDataForDraftCorrespondence(correspondenceContent, attachments, replyingCorrespondenceId));
    }

    /**
     * Updates the draft correspondence
     * @param caseId the id of the case that correspondence belongs
     * @param correspondenceContent the content of the draft correspondence
     * @param attachments The attachments
     * @param replyingCorrespondenceId the id of the draft correspondence
     */
    updateDraftCorrespondence(caseId: number, correspondenceContent: string,
                              attachments: DocumentDetailsDto[], replyingCorrespondenceId: number): Observable<CaseDetailsDto> {
        return this.http.put<CaseDetailsDto>(`api/portal/correspondence/${ caseId }/draft-correspondence`,
            this.createFormDataForDraftCorrespondence(correspondenceContent, attachments, replyingCorrespondenceId));
    }

    /**
     * Delete the given draft correspondence
     * @param caseId the id of the case that correspondence belongs
     * @param correspondenceId the id of the draft correspondence
     */
    deleteDraftCorrespondence(caseId: number, correspondenceId: number): Observable<CaseDetailsDto> {
        return this.http.delete<CaseDetailsDto>(`api/portal/correspondence/${ caseId }/draft-correspondence?draftCorrespondenceId=${ correspondenceId }`);
    }

    sendDraftCorrespondence(caseId: number, message: CorrespondenceDto, replyMessage: {
        content: string,
        attachments: DocumentDetailsDto[]
    }): Observable<CaseDetailsDto> {

        const formData = new FormData();
        const draftCorrespondenceDocuments = new Array<DocumentDetailsDto>();

        formData.append('caseId', new Blob([JSON.stringify(caseId)], { type: 'application/json' }));
        formData.append('message', new Blob([replyMessage.content], { type: 'application/json' }));

        /**
         * If there are attachments
         */
        if (replyMessage.attachments !== null) {
            replyMessage.attachments.forEach(attachment => {
                // If there is documentId it means that is an old attachment
                if (attachment.documentId) {
                    draftCorrespondenceDocuments.push(attachment);
                } else if (attachment instanceof File) {
                    const file = attachment as File;
                    formData.append('files', file, file.name);
                } else {
                    formData.append('files', attachment.binData, attachment.binData.name);
                }
            });
        }

        formData.append('draftCorrespondenceDocuments',
            new Blob([JSON.stringify(draftCorrespondenceDocuments)], { type: 'application/json' }));

        // If message is standalone draft correspondence we send null else send the caseCorrespondenceId
        if (message.isDraftCorrespondence) {
            formData.append('caseCorrespondenceRequestId',
                new Blob([JSON.stringify(null)], { type: 'application/json' }));
            formData.append('draftCorrespondenceId',
                new Blob([JSON.stringify(message.correspondenceId)], { type: 'application/json' }));
        } else {
            formData.append('caseCorrespondenceRequestId',
                new Blob([JSON.stringify(message.caseCorrespondenceId)], { type: 'application/json' }));

            // If message has draft correspondence we send the draftCorrespondenceId else null
            if(message.draftCorrespondenceId) {
                formData.append('draftCorrespondenceId',
                    new Blob([JSON.stringify(message.draftCorrespondenceId)], { type: 'application/json' }));
            } else {
                formData.append('draftCorrespondenceId',
                    new Blob([JSON.stringify(null)], { type: 'application/json' }));
            }
        }

        return this.http.post<CaseDetailsDto>('api/portal/correspondence/reply', formData);
    }

    /**
     * Discard the draft confirmatory request of a case
     * @param caseId the id of the case
     */
    discardDraftConfirmatoryRequest(caseId: number): Observable<void>  {
        return this.http.delete<void>(`api/portal/case/${caseId}/draft-confirmatory`);
    }

    /**
     * Get the confirmatory draft request of a case
     * @param caseId the id of the case
     */
    loadDraftConfirmatoryRequest(caseId: number): Observable<DraftConfirmatoryPortalDto> {
        return this.http.get<DraftConfirmatoryPortalDto>(`api/portal/case/${caseId}/draft-confirmatory/detail`);
    }

    /**
     * Create a new confirmatory draft request.
     * @param caseId the case identifier.
     * @param draft contains caseId and requestMessage.
     */
    createDraftConfirmatoryRequest(caseId: number, draft: DraftConfirmatoryPortalDto): Observable<DraftConfirmatoryPortalDto> {
        return this.http.post<DraftConfirmatoryPortalDto>(`api/portal/case/${caseId}/draft-confirmatory`,
            draft);
    }

    /**
     * Update confirmatory draft request
     * @param caseId the case identifier.
     * @param draft contains caseId and requestMessage.
     */
    updateDraftConfirmatoryRequest(caseId: number, draft: DraftConfirmatoryPortalDto): Observable<DraftConfirmatoryPortalDto> {
        return this.http.put<DraftConfirmatoryPortalDto>(`api/portal/case/${caseId}/draft-confirmatory`,
            draft);
    }

    /**
     * Updates case's subject
     * @param caseId
     * @param subject
     */
    updateSubject(caseId: number, subject: string): Observable<void> {
        return this.http.put<void>(`api/portal/case/${ caseId }/subject`, subject);
    }

    /**
     * Util method to construct formData object for creation/update correspondenceDraft
     * @param correspondenceContent the draft content of the correspondance
     * @param attachments  the correspondence attachemnts provided from the applicant
     * @param correspondenceId the id of the correspondence that the draft content belongs
     */
    private createFormDataForDraftCorrespondence(correspondenceContent: string,
                                                 attachments: DocumentDetailsDto[], correspondenceId: number) {
        const formData = new FormData();
        const correspondencePortalDto = {};
        correspondencePortalDto['attachments'] = new Array<DocumentDetailsDto>();
        correspondencePortalDto['content'] = correspondenceContent;
        correspondencePortalDto['correspondenceId'] = correspondenceId;
        /**
         * If there are attachments
         */
        if (attachments) {
            attachments.forEach(attachment => {
                // If there is documentId it means that is an old attachment
                if (attachment.documentId) {
                    correspondencePortalDto['attachments'].push(attachment);
                } else if (attachment instanceof File) {
                    const file = attachment as File;
                    formData.append('files', file, file.name);
                } else {
                    formData.append('files', attachment.binData, attachment.binData.name);
                }
            });
        }
        formData.append('correspondencePortalDto', new Blob([JSON.stringify(correspondencePortalDto)], { type: 'application/json' }));
        return formData;
    }

}
